import { inject, injectable } from 'tsyringe'
import Action from '@/common/action'
import { Howl, Howler, SoundSpriteDefinitions } from 'howler'
import { MathUtils } from 'three'

import type { ISound, SoundMapItem } from '@/shared/di/interface/sound'

@injectable()
export class SoundManager implements ISound {
  soundMaps: Map<string, SoundMapItem[]> = new Map<string, SoundMapItem[]>()
  runningLoops: Map<string, Howl> = new Map<string, Howl>()
  onContextStateChanged = new Action<string>()
  #globalMute = false

  constructor() {
    Howler.volume(0.3)

    this.onWebAudioContextChanged = this.onWebAudioContextChanged.bind(this)
  }

  onWebAudioContextChanged(event: any): void {
    this.onContextStateChanged.invoke(event.target.state)
  }

  registerSoundMap(id: string, map: any): void {
    if (map) {
      this.soundMaps.set(id, map as SoundMapItem[])
    }
  }

  unregisterSoundMap(id: string): void {
    //TODO: unload from cache
    this.soundMaps.delete(id)
    this.runningLoops.clear()
  }

  setSoundVolume(howl: Howl, volume: number): void {
    try {
      if (Howler.ctx.state === 'running') {
        howl.volume(volume)
      }
    } catch (err) {
      console.log('could not set voulme')
    }
  }

  setLoopVolume(id: string, volume: number): void {
    try {
      if (Howler.ctx.state === 'running') {
        const snd = this.runningLoops.get(id)
        if (snd) {
          snd.volume(volume)
        }
      }
    } catch (err) {
      console.log('could not set voulme')
    }
  }

  play(id: string, volume = 1, spriteId = ''): Howl | undefined {
    if (this.#globalMute) {
      return
    }
    let soundUrl = `sounds/${id}.mp3`
    let loop = false
    let sprite: SoundSpriteDefinitions | undefined

    const soundMapItem = this.getMapItem(id)
    if (soundMapItem) {
      if (soundMapItem.file.includes('http')) {
        soundUrl = soundMapItem.file
      } else {
        soundUrl = `sounds/${soundMapItem.file}.mp3`
      }

      if (soundMapItem.loop) {
        loop = true
      }

      if (soundMapItem.volume) {
        volume = soundMapItem.volume
      }

      if (soundMapItem.randomVolume) {
        volume *= 1 - Math.random() * soundMapItem.randomVolume
      }

      volume = MathUtils.clamp(volume, 0, 1)

      if (soundMapItem.sprite) {
        sprite = soundMapItem.sprite as SoundSpriteDefinitions
      }
    } else {
      console.log('no sound in map with id:' + id)
    }

    const h = new Howl({
      src: soundUrl,
      rate: 1,
      loop,
      volume: volume,
      sprite: sprite
    })

    if (spriteId !== '') {
      h.play(spriteId)
    } else {
      h.play()
    }

    return h
  }

  startLoop(id: string): Howl {
    this.stopLoop(id)

    let soundUrl = `sounds/${id}.wav`

    let volume = 0

    const soundMapItem = this.getMapItem(id)
    if (soundMapItem) {
      if (soundMapItem.file.includes('http')) {
        soundUrl = soundMapItem.file
      } else {
        soundUrl = `sounds/${soundMapItem.file}.mp3`
      }

      if (soundMapItem.volume) {
        volume = soundMapItem.volume
      }
    } else {
      console.log('no sound in map with id:' + id)
    }

    const h = new Howl({
      src: soundUrl,
      loop: true,
      volume: volume
    })

    h.play()

    this.runningLoops.set(id, h)

    return h
  }

  stopLoop(id: string) {
    const exisitingLoop = this.getLoop(id)
    if (exisitingLoop) {
      exisitingLoop.stop()
      exisitingLoop.unload()
      this.runningLoops.delete(id)
    }
  }

  getLoop(id: string): Howl | undefined {
    return this.runningLoops.get(id)
  }

  private getMapItem(id: string): SoundMapItem | undefined {
    let foundItem = undefined
    this.soundMaps.forEach((value: SoundMapItem[]) => {
      const searchItem = value.find(x => x.id === id)
      if (searchItem) {
        foundItem = searchItem
      }
    })
    return foundItem
  }

  private onAppActiveChanged = (isActive: boolean) => {
    if (isActive) {
      this.resetAudioContext()

      if (!this.#globalMute) {
        Howler.mute(false)
      }

      const restartIds: string[] = []

      this.runningLoops.forEach((value, key) => {
        restartIds.push(key)
      })

      restartIds.forEach(id => {
        this.startLoop(id).volume(0)
        this.setLoopVolume(id, 0)
      })
    } else {
      this.runningLoops.forEach((value, key) => {
        value.stop()
      })

      Howler.mute(true)
    }
  }

  setGlobalMute(value: boolean): void {
    this.#globalMute = value
    if (this.#globalMute) {
      Howler.mute(true)
    } else {
      Howler.mute(false)
    }
  }

  resetAudioContext(): void {
    Howler.ctx.onstatechange = null
    Howler.unload()
    Howler.mute(this.#globalMute)
    Howler.ctx.onstatechange = this.onWebAudioContextChanged
    Howler.ctx.suspend()
    Howler.ctx.resume()
  }
}
